import React, { useState, useRef, useCallback, useEffect } from 'react';
import Form, {
  Item,
  EmptyItem,
  Label,
  ButtonItem,
  ButtonOptions,
  RequiredRule
} from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import notify from 'devextreme/ui/notify';
import { useAuth } from '../../contexts/auth';
import { useHistory, Link } from 'react-router-dom';
import Dict from "../../messages"; 
import validationStore from '../../store/validationStore';
import BuildStampLabel from '../BuildStampLabel';
import { BUILD_VERSION, markBuildStampSeen } from '../../utils/appBuildStamp';
import { getRemainingLockMs, isLoginLocked, readLoginLockState } from '../../utils/loginLockState';
import './login-form.scss';


export default function LoginForm() {


  const { signIn } = useAuth();
  const [loading, setLoading] = useState(false);
  const [lockUntil, setLockUntil] = useState(0);
  const formData = useRef({});
  const rememberMeRef = useRef(false);
  const history = useHistory();

  // On mount, check if username is remembered and load validation data
  useEffect(() => {
    const remembered = localStorage.getItem('remembered_username');
    if (remembered) {
      formData.current.username = remembered;
      rememberMeRef.current = true;
    }
    
    // Validasyon verilerini yükle (login'de bir kere)
    validationStore.loadValidationData();
  }, []);


  const usernameValue = String(formData.current.username || '').trim();
  const activeLockUntil = Number(lockUntil || readLoginLockState(usernameValue)?.lockUntil || 0);
  const loginLocked = activeLockUntil > 0 && isLoginLocked(activeLockUntil);
  const remainingSeconds = loginLocked ? Math.ceil(getRemainingLockMs(activeLockUntil) / 1000) : 0;

  useEffect(() => {
    if (!loginLocked) return undefined;
    const timer = window.setInterval(() => {
      const state = readLoginLockState(usernameValue || '');
      const nextLockUntil = Number(state?.lockUntil || 0);
      if (!nextLockUntil || !isLoginLocked(nextLockUntil)) {
        setLockUntil(0);
        return;
      }
      setLockUntil(nextLockUntil);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [loginLocked, usernameValue]);

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    const { username: rawUsername, password } = formData.current;
    const currentUsernameValue = String(rawUsername || '').trim();
    const lockState = readLoginLockState(currentUsernameValue || '');
    const lockUntilNow = Number(lockState?.lockUntil || 0);
    if (lockUntilNow > 0 && isLoginLocked(lockUntilNow)) {
      setLockUntil(lockUntilNow);
      notify('3 kez hatalı giriş yaptınız. Lütfen 1 dakika sonra tekrar deneyin.', 'error', 2000);
      return;
    }
    if (!password || String(password).trim() === '') {
      notify(Dict.get('Şifre zorunlu'), 'error', 2000);
      return;
    }
    setLoading(true);
    const result = await signIn(currentUsernameValue, password);
    setLoading(false);
    if (!result.isOk) {
      const nextLockUntil = Number(result.lockedUntil || readLoginLockState(currentUsernameValue || '')?.lockUntil || 0);
      if (nextLockUntil > 0 && isLoginLocked(nextLockUntil)) {
        setLockUntil(nextLockUntil);
      } else {
        setLockUntil(0);
      }
      notify(result.message || Dict.get('Kullanıcı adı veya şifre hatalı!'), 'error', 2000);
      return;
    }
    // Remember username if checked, else remove
    if (rememberMeRef.current && currentUsernameValue) {
      localStorage.setItem('remembered_username', currentUsernameValue);
    } else {
      localStorage.removeItem('remembered_username');
    }
    markBuildStampSeen(BUILD_VERSION);
    try { history.push('/'); } catch (e) { /* ignore */ }
  }, [signIn, history]);

  // Define editor options for rememberMe checkbox
  const rememberMeEditorOptions = {
    text: Dict.get('Beni Hatırla'),
    value: rememberMeRef.current,
  };

  return (
    <form className={'login-form'} onSubmit={onSubmit}>
      <h2 className="login-form-title">{Dict.get('Giriş Yap')}</h2>
      {loginLocked && (
        <div style={{ marginBottom: 12, color: '#b42318', fontWeight: 600, textAlign: 'center' }}>
          3 kez hatalı giriş yaptınız. Lütfen {remainingSeconds} saniye sonra tekrar deneyin.
        </div>
      )}
      <br/>
      <Form formData={formData.current} disabled={loading || loginLocked}>
        <Item
          dataField={'username'}
          editorType={'dxTextBox'}
          editorOptions={{
            ...emailEditorOptions,
            value: formData.current.username || '',
            onValueChanged: (e) => { formData.current.username = e.value; }
          }}
        >
          <Label text={Dict.get('Kullanıcı Adı')} visible={true} />
        </Item>
        <Item
          dataField={'password'}
          editorType={'dxTextBox'}
          editorOptions={{
            ...passwordEditorOptions,
            value: formData.current.password || '',
            onValueChanged: (e) => { formData.current.password = e.value; },
          }}
        >
          <Label text={Dict.get('Şifre')} visible={true} />
        </Item>
        <EmptyItem />
        <Item
          dataField={'rememberMe'}
          editorType={'dxCheckBox'}
          editorOptions={{
            ...rememberMeEditorOptions,
            value: rememberMeRef.current,
            onValueChanged: (e) => { rememberMeRef.current = e.value; }
          }}
        >
          <Label visible={false} />
        </Item>
        <EmptyItem />
        <ButtonItem>
          <ButtonOptions
            width={'100%'}
            type={'default'}
            useSubmitBehavior={true}
            disabled={loginLocked}
          >
            <span className="dx-button-text">
              {loading ? (
                <LoadIndicator width={'24px'} height={'24px'} visible={true} />
              ) : loginLocked ? (
                `1 dakika bekleyin`
              ) : (
                Dict.get('Giriş')
              )}
            </span>
          </ButtonOptions>
        </ButtonItem>
        <Item>
          <div className={'link'}>
            <Link to={'/reset-password'}>{Dict.get('Şifremi Unuttum')}</Link>
          </div>
        </Item>
      </Form>
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <BuildStampLabel />
      </div>
    </form>
  );
}

const emailEditorOptions = { stylingMode: 'filled', placeholder: Dict.get('Kullanıcı'), mode: 'username' };
const passwordEditorOptions = { stylingMode: 'filled', placeholder: Dict.get('Şifre'), mode: 'password' };